// packages/web-ui/src/sse/attach-to-chat-slice.ts
//
// W2-S5 AC #10 — SSE → chat-slice dispatch glue.
// W2-S6-integration — adds inline-card attach for `kb:viewRendered`,
//   `kb:changesetProposed`, and clarification (carried in the
//   `apply_model_change` tool's `chat:toolResult` output JSON, NOT via a
//   dedicated `request_clarification` tool — the story spec assumed a
//   tool by that name; verification per `packages/base/src/agent/tools/`
//   confirmed clarification arrives as a SUCCESS short-circuit on
//   `apply_model_change` with `output.clarificationNeeded === true`).
// W3 fix-pass HIGH-1 / HIGH-4 — wires `validation:result` into the default
//   diagnostics store (so the file-viewer's force-linting subscription has
//   data to react to) and `files:changed` into the default files store
//   (1 s pulse-on-change UX for the file-tree). Save-gate `respondToFilesChanged`
//   subscribers register through `addFilesChangedSubscriber` from this module
//   because the gate is a per-mount hook — the registry pattern lets the
//   dispatcher call into a per-file-viewer hook closure without a hard import.
//
// Maps the canonical SSE event names from Arch §3.3 onto the corresponding
// chat-slice actions. Events not relevant to the chat panel are explicit
// NOOPs so the dev agent can see exactly which events are deferred to
// other stories.

import type { ChatSlice } from '../state/chat.js';
import type { SseSlice } from '../state/sse.js';
import type {
  InlineCardEnvelope,
  InlineCardsSlice,
  EntityConnectionsRow,
} from '../state/inline-cards.js';
import type { ChangesetSlice } from '../state/changeset.js';
import type { KbSlice } from '../state/kb.js';
import type { ViewsSlice } from '../state/views.js';
import type { ImageViewsSlice } from '../state/image-views.js';
import type { EventLogSlice, EventLogSource } from '../state/eventLog.js';
import type { SseEvent } from './client.js';
import type { Diagnostic } from '@tessa-lean/base/api-schemas/validation.js';
import type { KbApplyOutcome, KbChangesetProposedPayload } from '@tessa-lean/base';
import { getDefaultDiagnosticsStore } from '../state/diagnostics.js';
import { getFilesStore } from '../state/files.js';
import { getChangesetPreviewsStore } from '../state/changeset-previews.js';
import { sessionsStoreSingleton } from '../state/sessions-store-singleton.js';
import { getDefaultSettingsStore, selectMergedByKey } from '../state/settings.js';
import { qualifiedKey } from '@tessa-lean/base';
import type { ReactFlowLayoutJson } from '@tessa-lean/base/api-schemas/index.js';

export interface AttachOptions {
  /** Active session id — used to scope `appendToken` / `finishStream` etc. */
  getActiveSessionId: () => string | null;
}

/**
 * Refetch the sessions sidebar after a chat turn completes so a freshly-titled
 * / newly-populated session reconciles its title, `messageCount`, and sort
 * order with disk without waiting for a manual reload. Guarded so it only fires
 * once the list has actually been loaded (`status === 'ready'`), and tolerant
 * of the singleton being absent in test harnesses without the SPA shell.
 */
function refreshSessionsAfterTurn(): void {
  try {
    const store = sessionsStoreSingleton.getState();
    if (store.status !== 'ready') return;
    void store.refreshSessions();
  } catch {
    // Sessions singleton not present — the live sidebar refresh is best-effort.
  }
}

/**
 * Combined slice surface the dispatcher requires. The store may be the root
 * `useRootStore` or a vanilla composition built per-test; both expose the
 * same intersection because `state/store.ts` composes all the slices.
 *
 * `Partial<ChangesetSlice & KbSlice>` is intersected so the dispatcher can
 * fan W4 SSE events (e.g. `kb:changesetApplied`) into the W4 slices when
 * they are composed into the root store, while still accepting older test
 * stores that only expose chat/sse/inline-cards. Each W4 fan-out is
 * defensively guarded — the dispatcher checks `typeof fn === 'function'`
 * before invoking, so a partial composition silently no-ops.
 */
export interface ChatLikeStore {
  getState: () => ChatSlice &
    SseSlice &
    InlineCardsSlice &
    Partial<ChangesetSlice> &
    Partial<KbSlice> &
    Partial<ViewsSlice> &
    Partial<ImageViewsSlice> &
    Partial<EventLogSlice>;
}

/**
 * Wire a single `SseEvent` into the appropriate chat-slice / sse-slice
 * action. Returns `true` when the event was consumed (dispatched), `false`
 * when it was an explicit NOOP for this story.
 *
 * Exported as a pure function so unit tests can drive each event without
 * spinning up the React provider.
 */
export function dispatchSseEvent(
  store: ChatLikeStore,
  event: SseEvent,
  opts: AttachOptions,
): boolean {
  const state = store.getState();
  if (event.id !== undefined && event.id.length > 0) {
    state.setLastEventId(event.id);
  }

  // Migration final fix-pass — HIGH-3. Record the event into the eventLog
  // slice for the UtilityDrawer's Activity / Debug tabs. This is ADDITIVE
  // and never short-circuits the per-slice fan-out below; the existing
  // dispatch logic is unchanged. The eventLog slice may not be composed in
  // every test store, so the call is guarded with a typeof check.
  appendToEventLogIfAvailable(state, event);

  switch (event.event) {
    case 'chat:token': {
      const payload = asObject(event.data);
      const messageId = stringField(payload, 'messageId');
      const text = stringField(payload, 'text');
      if (messageId === null || text === null) return false;
      const sessionId = stringField(payload, 'sessionId') ?? opts.getActiveSessionId();
      state.appendToken({ messageId, text, sessionId: sessionId ?? undefined });
      return true;
    }

    case 'chat:toolCall': {
      const payload = asObject(event.data);
      const messageId = stringField(payload, 'messageId');
      const callId = stringField(payload, 'callId');
      const toolName = stringField(payload, 'toolName');
      if (messageId === null || callId === null || toolName === null) return false;
      const input = payload?.input;
      const sessionId = stringField(payload, 'sessionId') ?? opts.getActiveSessionId();
      state.recordToolCall({
        messageId,
        callId,
        toolName,
        input,
        sessionId: sessionId ?? undefined,
      });
      return true;
    }

    case 'chat:sessionRenamed': {
      // Server-side auto-titled this session from the first user message.
      // Update three surfaces in lockstep:
      //   - chat-slice SessionMeta → drives the active tab label + ChatPanel header
      //   - sessions singleton row → drives the SessionList row title
      const payload = asObject(event.data);
      const sessionId = stringField(payload, 'sessionId');
      const title = stringField(payload, 'title');
      if (sessionId === null || title === null || title.length === 0) return false;
      state.setSessionTitle(sessionId, title);
      try {
        sessionsStoreSingleton.getState().updateSessionTitle(sessionId, title);
      } catch {
        // Defensive — singleton may be absent in test harnesses without the SPA shell.
      }
      return true;
    }

    case 'chat:sessionUsage': {
      // Context-usage indicator (Composer footer). The server's
      // orchestrator-runner re-emits `agent:llmUsage` as this per-session
      // event with `{ sessionId, promptTokens, completionTokens }`. The
      // composer reads `tokensUsed` off `SessionMeta` and renders a
      // percentage against a fixed 128k context-window budget. We track
      // the high-watermark per session so the indicator doesn't dip
      // backwards when a small classifier/tool sub-call lands after the
      // main orchestrator turn.
      const payload = asObject(event.data);
      const sessionId = stringField(payload, 'sessionId');
      const promptTokensRaw = payload?.promptTokens;
      if (sessionId === null || typeof promptTokensRaw !== 'number') return false;
      const meta = state.sessions.find((m) => m.id === sessionId);
      const prior = meta?.tokensUsed ?? 0;
      const next = Math.max(prior, promptTokensRaw);
      state.setSessionMeta(sessionId, { tokensUsed: next });
      return true;
    }

    case 'chat:toolResult': {
      const payload = asObject(event.data);
      const callId = stringField(payload, 'callId');
      if (callId === null) return false;
      const statusRaw = stringField(payload, 'status') ?? 'ok';
      const status = (['ok', 'error', 'cancelled'] as const).includes(
        statusRaw as 'ok' | 'error' | 'cancelled',
      )
        ? (statusRaw as 'ok' | 'error' | 'cancelled')
        : 'ok';
      state.recordToolResult({
        callId,
        output: payload?.output,
        status,
        errorMessage: stringField(payload, 'errorMessage') ?? undefined,
      });
      // Terminal `respond_to_user` tool result also finishes the stream.
      const toolName = stringField(payload, 'toolName');
      if (toolName === 'respond_to_user') {
        const sid = opts.getActiveSessionId();
        if (sid !== null) state.finishStream(sid);
      }
      // W2-S6-integration — clarification short-circuit. The orchestrator's
      // `apply_model_change` tool (no dedicated `request_clarification` tool
      // exists; verification per packages/base/src/agent/tools/) returns a
      // SUCCESS result whose `output` carries `{stage: 'clarification',
      // clarificationNeeded: true, options: string[]}`. When we see that
      // shape, attach a clarification envelope to the current streaming
      // assistant message so the user sees the question inline.
      const clarificationEnv = extractClarificationEnvelope(payload?.output);
      if (clarificationEnv !== null) {
        const targetMessageId = locateAttachTarget(state, opts);
        if (targetMessageId !== null) {
          state.attachCard(targetMessageId, clarificationEnv);
        }
      }
      return true;
    }

    case 'system:notice': {
      const payload = asObject(event.data);
      const level = stringField(payload, 'level');
      const message = stringField(payload, 'message') ?? '';
      // `system:notice` is broadcast via SseHub.broadcastAll, so every tab
      // receives every notice regardless of which chat turn produced it.
      // The server tags `streamComplete` with the originating sessionId
      // so we can finalise the correct turn even when the user has
      // multiple parallel chats running — without this, the focused tab
      // eats every turn's `streamComplete` and the background turns spin
      // forever.
      const payloadSid = stringField(payload, 'sessionId');
      const activeSid = opts.getActiveSessionId();
      const sid = payloadSid ?? activeSid;
      if (level === 'streamComplete') {
        if (sid !== null) state.finishStream(sid);
        // A turn just landed: the server has already persisted the new
        // message(s) and (on the first turn) the auto-generated title. Refetch
        // the sessions list so the sidebar row reconciles its title,
        // `messageCount`, and sort order with disk instead of lingering on the
        // stale "Untitled / 0 messages" it showed before the turn. Only when
        // the list has actually been loaded (`ready`) — otherwise this no-ops
        // so contexts that never opened the sidebar don't eagerly fetch.
        refreshSessionsAfterTurn();
        return true;
      }
      // LV3-S4 — engine-escalation control notice. When a view auto-escalates its
      // sticky lead to the raster, the server broadcasts a `system:notice` whose
      // message is `engine-escalation:<reason>:<viewId>` (the reason rides no
      // manifest field nor `image:*` payload — this notice is the ONLY carrier).
      // Patch the matching response-view card's envelope with `escalated=<reason>`
      // (and bump `layoutRevision` so the frozen card re-reaches the new prop),
      // then short-circuit: this is an internal control signal, NOT user-facing
      // chat text (the human-readable ack is posted separately as an assistant
      // message), so we must NOT `appendSystemMessage` it. The companion
      // `engine-escalation-suppressed:raster-off:<viewId>` notice (raster OFF ⇒ no
      // escalation lead) is likewise swallowed without marking the card escalated.
      const escalation = parseEscalationNotice(message);
      if (escalation !== null) {
        if (escalation.reason !== null) {
          markCardEscalated(state, escalation.viewId, escalation.reason);
        }
        return true;
      }
      if (sid !== null && message.length > 0) {
        state.appendSystemMessage(sid, message);
      }
      return true;
    }

    case 'error': {
      const sid = opts.getActiveSessionId();
      if (sid !== null) state.finishStream(sid);
      state.markDisconnected();
      return true;
    }

    case 'kb:viewRendered': {
      // W2-S6-integration — attach a `response-view-proposal` envelope to
      // the current streaming assistant message. The card's auto-fetch
      // effect kicks the SVG render via the integration agent's
      // `onFetchSvg` callback.
      //
      // 77k1 Defect A defensive guard — when the server tags the event
      // with a `sessionId` that doesn't match the SPA's active session,
      // drop the attachment. The SseHub already fans per-session via the
      // `sessionId=` query param, but during a session-switch window
      // ring-buffered events from the prior session may still ride the
      // last-event-id replay on the new connection and end up on the new
      // session's message bubble. Defensive — only blocks the rare race.
      const payload = asObject(event.data);
      const viewId = stringField(payload, 'id');
      if (viewId === null) return false;
      if (!eventTargetsActiveSession(payload, opts)) return false;
      // Fan the same event into the views slice so the Views context-panel
      // refreshes its list as soon as the server writes/updates a view. The
      // slice is composed into the root store (see `state/store.ts`); test
      // stores that omit the slice silently no-op via the Partial guard.
      const refreshViewsList = (state as Partial<ViewsSlice>).refreshAfterListChanged;
      if (typeof refreshViewsList === 'function') {
        try {
          void refreshViewsList();
        } catch {
          // Defensive — never propagate slice errors out of the SSE loop.
        }
      }

      // ── Re-layout / re-scope re-emit path ───────────────────────────────
      // A "adjust layout" / scope request (from the inline card, a chat tweak,
      // or the adjust_diagram tool) re-runs relayoutView / scopeView server-side
      // and re-emits `kb:viewRendered` for the SAME viewId. `attachCard` is a
      // replay-safe no-op once a card exists for a view, so the inline preview
      // would never re-fetch the new `layout.json`; we must instead bump the
      // EXISTING card's `layoutRevision` to re-key its one-shot fetch guard and
      // re-render the updated arrangement IN PLACE. Critically, that card may
      // live on an EARLIER bubble than the turn now streaming (the view was
      // proposed earlier and the tweak/tool targets it later), so resolve the
      // hosting message by the STABLE viewId across ALL bubbles — not the
      // current turn's attach target (the old wrong-bubble bug where the update
      // landed on a new, empty bubble). First emit (no hosting card) falls
      // through to the attach path below.
      const hostingMessageId = locateMessageHostingView(state, viewId, opts);
      if (hostingMessageId !== null) {
        const cards = state.cardsByMessageId[hostingMessageId] ?? [];
        const idx = cards.findIndex(
          (e) => e.kind === 'response-view-proposal' && e.props.viewId === viewId,
        );
        if (idx >= 0) {
          const existing = cards[idx];
          const priorRevision =
            existing !== undefined &&
            existing.kind === 'response-view-proposal' &&
            typeof existing.props.layoutRevision === 'number'
              ? existing.props.layoutRevision
              : 0;
          state.setCardProps(hostingMessageId, idx, {
            layoutRevision: priorRevision + 1,
          });
          return true;
        }
      }

      // ── First emit ──────────────────────────────────────────────────────
      // No bubble hosts this view yet — attach a fresh card to the current
      // turn's streaming/pending assistant bubble.
      const targetMessageId = locateAttachTarget(state, opts);
      if (targetMessageId === null) return false;
      // The view payload doesn't carry a friendly name; fall back to the
      // viewId so the card has something to render until the integration's
      // onFetchSvg-driven layout reveals the real name. Element count is
      // unknown at SSE time (the layoutPath / svgPath are paths, not
      // counts); leave undefined.
      //
      // LV3-S1 end-to-end wiring — gate the "Generate LLM visualization"
      // button. `rasterOffered` rides on the SSE payload (the server's
      // `NodeAutoViewWriter` computes offer-eligibility before the emit);
      // `llmImageEnabled` mirrors the effective `views.llmImage.enabled`
      // setting. The card gates the button on `rasterOffered === true &&
      // llmImageEnabled === true`. Both default falsy when absent so the
      // switch-OFF / legacy path renders NO button (byte-identical to before
      // this wiring). MINOR fix — `llmImageEnabled` is only meaningful when
      // `rasterOffered` is true, so read the merged-settings store only inside
      // that branch (avoids an unnecessary per-event settings-store read on the
      // common no-offer path).
      const rasterOffered = payload?.rasterOffered === true;
      const llmImageEnabled = rasterOffered ? readLlmImageEnabled() : false;
      const envelope: InlineCardEnvelope = {
        kind: 'response-view-proposal',
        props: {
          viewId,
          viewName: viewId,
          ...(rasterOffered ? { rasterOffered: true } : {}),
          ...(llmImageEnabled ? { llmImageEnabled: true } : {}),
        },
      };
      state.attachCard(targetMessageId, envelope);
      return true;
    }

    case 'kb:entityConnections': {
      // Deterministic connections-table card — attach the COMPLETE, KB-derived
      // neighbour set to the current assistant message. Mirrors the
      // `kb:viewRendered` attach path (session guard + locateAttachTarget +
      // idempotent attachCard). The rows are authoritative; the LLM's prose
      // sits above the card and no longer has to reproduce the table.
      const payload = asObject(event.data);
      const target = stringField(payload, 'target');
      const targetQualifiedName = stringField(payload, 'targetQualifiedName');
      if (target === null || targetQualifiedName === null) return false;
      if (!eventTargetsActiveSession(payload, opts)) return false;
      const rawRows = payload?.rows;
      if (!Array.isArray(rawRows)) return false;
      const rows: EntityConnectionsRow[] = rawRows.flatMap((r): EntityConnectionsRow[] => {
        const row = asObject(r);
        const neighbour = stringField(row, 'neighbour');
        const entityId = stringField(row, 'entityId');
        const direction = stringField(row, 'direction');
        if (
          neighbour === null ||
          entityId === null ||
          (direction !== 'incoming' && direction !== 'outgoing')
        ) {
          return [];
        }
        const flowTypes = Array.isArray(row?.['flowTypes'])
          ? (row!['flowTypes'] as unknown[]).filter((t): t is string => typeof t === 'string')
          : [];
        const ports = Array.isArray(row?.['ports'])
          ? (row!['ports'] as unknown[]).map((p) => {
              const po = asObject(p);
              return {
                targetPort: stringField(po, 'targetPort') ?? '',
                neighbourPort: stringField(po, 'neighbourPort') ?? '',
              };
            })
          : [];
        const level = stringField(row, 'level');
        const built: EntityConnectionsRow = { neighbour, entityId, direction, flowTypes, ports };
        if (level !== null) built.level = level;
        return [built];
      });
      if (rows.length === 0) return false;
      const targetMessageId = locateAttachTarget(state, opts);
      if (targetMessageId === null) return false;
      const edgeCountRaw = payload?.['edgeCount'];
      const edgeCount = typeof edgeCountRaw === 'number' ? edgeCountRaw : rows.length;
      const envelope: InlineCardEnvelope = {
        kind: 'entity-connections',
        props: { target, targetQualifiedName, rows, edgeCount },
      };
      state.attachCard(targetMessageId, envelope);
      return true;
    }

    case 'kb:dataTable': {
      // Generic deterministic data-table card — attach the COMPLETE, KB-derived
      // {columns, rows} table to the current assistant message. Same attach path
      // as `kb:entityConnections` (session guard + locateAttachTarget +
      // idempotent attachCard keyed on the server-supplied cardId).
      const payload = asObject(event.data);
      const cardId = stringField(payload, 'cardId');
      const title = stringField(payload, 'title');
      if (cardId === null || title === null) return false;
      if (!eventTargetsActiveSession(payload, opts)) return false;
      const rawColumns = payload?.['columns'];
      const rawRows = payload?.['rows'];
      if (!Array.isArray(rawColumns) || !Array.isArray(rawRows)) return false;
      const columns = rawColumns.filter((c): c is string => typeof c === 'string');
      if (columns.length === 0) return false;
      const rows: string[][] = rawRows
        .filter((r): r is unknown[] => Array.isArray(r))
        .map((r) => r.map((cell) => (typeof cell === 'string' ? cell : String(cell ?? ''))));
      if (rows.length === 0) return false;
      const targetMessageId = locateAttachTarget(state, opts);
      if (targetMessageId === null) return false;
      const caption = stringField(payload, 'caption');
      const envelope: InlineCardEnvelope = {
        kind: 'data-table',
        props: { cardId, title, columns, rows, ...(caption !== null ? { caption } : {}) },
      };
      state.attachCard(targetMessageId, envelope);
      return true;
    }

    case 'image:genStarted': {
      // IV3-S1 — mount a skeleton 'illustrative-image' card IMMEDIATELY,
      // bound to the stable `imageViewId` correlation id on the PRODUCING
      // turn (resolved ONCE here from `turnCorrelationId`). `image:genReady`
      // later updates the SAME card purely by `imageViewId` — never
      // re-resolving an attach target (the wrong-bubble fix).
      const payload = asObject(event.data);
      const imageViewId = stringField(payload, 'imageViewId');
      const turnCorrelationId = stringField(payload, 'turnCorrelationId');
      if (imageViewId === null || turnCorrelationId === null) return false;
      // Cross-session contamination guard (mirrors the kb:* fan-outs).
      if (!eventTargetsActiveSession(payload, opts)) return false;
      const sessionId = stringField(payload, 'sessionId') ?? opts.getActiveSessionId();
      // Resolve the message to mount the skeleton on.
      //
      // PRIMARY (`locateMessageHostingView`): the message that already hosts
      // this view's card, matched by the STABLE viewId (the route sets
      // `imageViewId === viewId`). This is the ONLY branch that works for the
      // explicit "Generate LLM visualization" button (`POST /views/:viewId/image`),
      // which fires LONG AFTER the producing turn finished: its `turnCorrelationId`
      // is a fresh server randomUUID that matches no message, and `finishStream`
      // has already nulled the streaming/pending pointers that `locateTurnMessage`
      // falls back to — so `locateTurnMessage` alone returns null and the skeleton
      // (and the later image) were silently dropped.
      //
      // FALLBACK (`locateTurnMessage`): the retired IV-era in-turn auto-fire path,
      // where the raster began DURING streaming (a live bubble existed) — kept for
      // back-compat with any producer that fires within the turn.
      const targetMessageId =
        locateMessageHostingView(state, imageViewId, opts) ??
        locateTurnMessage(state, turnCorrelationId, opts);
      if (targetMessageId === null) return false;

      // Begin the image-views record (idempotent on imageViewId — SSE replay
      // safe). Defensively guarded like the kb:* slice fan-outs.
      const beginGen = (state as Partial<ImageViewsSlice>).beginGen;
      if (typeof beginGen === 'function') {
        try {
          beginGen({
            imageViewId,
            turnCorrelationId,
            sessionId: sessionId ?? null,
            messageId: targetMessageId,
          });
        } catch {
          // Defensive — never let a slice error break the SSE loop.
        }
      }

      // Attach the skeleton envelope (idempotent on the illustrative-image
      // identity key — genReady never re-appends a second card).
      const env: InlineCardEnvelope = {
        kind: 'illustrative-image',
        props: { imageViewId, viewId: imageViewId },
      };
      state.attachCard(targetMessageId, env);
      return true;
    }

    case 'image:genReady': {
      // IV3-S1 — UPDATE the existing card by `imageViewId`. CRITICALLY this
      // does NOT re-resolve any attach target: the inline-cards envelope was
      // mounted on the producing turn at genStarted, and the live status is
      // updated purely through the image-views slice keyed by imageViewId.
      // This is the wrong-bubble fix — a late image cannot land on the
      // latest bubble because we never look at "latest" here.
      const payload = asObject(event.data);
      const imageViewId = stringField(payload, 'imageViewId');
      const path = stringField(payload, 'path');
      if (imageViewId === null || path === null) return false;
      // AR-6 — active-session guard. A cross-session ring-replay landing
      // during a session-switch race must not resolve a card on the wrong
      // session. The slice's `resolveReady` is ALSO a strict no-op on an
      // absent record (belt-and-suspenders), so an orphan card can never be
      // minted from a replayed genReady.
      if (!eventTargetsActiveSession(payload, opts)) return false;
      const mimeType = stringField(payload, 'mimeType') ?? undefined;
      const alt = stringField(payload, 'alt') ?? undefined;
      const sceneContentHash = stringField(payload, 'sceneContentHash') ?? undefined;
      const resolveReady = (state as Partial<ImageViewsSlice>).resolveReady;
      if (typeof resolveReady === 'function') {
        try {
          resolveReady({ imageViewId, path, mimeType, alt, sceneContentHash });
        } catch {
          // Defensive — never let a slice error break the SSE loop.
        }
      }
      return true;
    }

    case 'image:genFailed': {
      // MAJ-3 — terminal failure of an in-flight raster generation. The
      // server (Unit A) emits `{ imageViewId, turnCorrelationId, sessionId?,
      // reason }` when the raster engine errors or the provider rejects. We
      // flip the existing card OUT of its spinning 'generating' state into a
      // non-spinning error/fallback state via the image-views slice's
      // `resolveError` — which RETAINS the prior (deterministic) image and
      // clears the pending edit. The card NEVER stays in an infinite spinner;
      // the panel's client-side timeout is a second backstop for the case
      // where neither genReady nor genFailed ever arrives.
      const payload = asObject(event.data);
      const imageViewId = stringField(payload, 'imageViewId');
      if (imageViewId === null) return false;
      // Active-session guard (same rationale as genReady) — a replayed
      // genFailed must not flip a card on the newly-active session.
      if (!eventTargetsActiveSession(payload, opts)) return false;
      const reason = stringField(payload, 'reason') ?? 'IMAGE_GENERATION_FAILED';
      const resolveError = (state as Partial<ImageViewsSlice>).resolveError;
      if (typeof resolveError === 'function') {
        try {
          resolveError({ imageViewId, errorCode: reason });
        } catch {
          // Defensive — never let a slice error break the SSE loop.
        }
      }
      return true;
    }

    case 'kb:changesetProposed': {
      // W2-S6-integration — attach a `kb-change-tree-shell` envelope. The
      // payload shape is `{changeset, entityStates, previewBaseUrl}` per
      // server's `_buildChangesetProposedMessage` (Arch §3.3 + base
      // KbChangesetProposedPayload).
      //
      // Tonight's fix (2026-05-17 E2E follow-up, action item #1): also
      // populate the changeset slice's `changesetsById` map by calling
      // `applyChangesetProposed`. The DiffMode side-panel subscribes to
      // the count of that map and refreshes its summary list when it
      // changes; without this call the Diff destination's side panel
      // stayed empty even though the proposal succeeded server-side.
      //
      // 77k1 Defect A defensive guard — same rationale as `kb:viewRendered`
      // above. The chat-side inline-card attach is per-session; events
      // tagged with a non-active sessionId would otherwise land on the
      // newly-active session's bubble during a session-switch race.
      // The DiffTab pulls from `changesetsById` (a workspace-global map)
      // independently, so we must still admit non-active events into the
      // changeset slice so the Diff tab list stays complete. Only the
      // inline-card attach is gated.
      const payload = asObject(event.data);
      const changeset = asObject(payload?.changeset);
      const changesetId = stringField(changeset, 'id');
      if (changesetId === null) return false;
      const eventForActiveSession = eventTargetsActiveSession(payload, opts);
      const entitiesRaw = changeset?.entities;
      const entityCount = Array.isArray(entitiesRaw) ? entitiesRaw.length : 0;
      // Fan out to the changeset slice when it is composed in this store
      // (guarded — the test stores may not include it). The payload
      // matches `KbChangesetProposedPayload` because the server emits
      // exactly that shape on this event.
      if (
        typeof state.applyChangesetProposed === 'function' &&
        changeset !== null &&
        Array.isArray(payload?.entityStates)
      ) {
        try {
          state.applyChangesetProposed(
            payload as unknown as KbChangesetProposedPayload,
          );
        } catch {
          // defensive — never let the SSE dispatcher throw.
        }
      }
      if (!eventForActiveSession) return true;
      const targetMessageId = locateAttachTarget(state, opts);
      if (targetMessageId === null) return false;
      const envelope: InlineCardEnvelope = {
        kind: 'kb-change-tree-shell',
        props: { changesetId, entityCount },
      };
      state.attachCard(targetMessageId, envelope);
      return true;
    }

    case 'kb:changesetPreview': {
      // Color-coded visual diff card sourced from
      // `synthesizeChangesetPreview` on the server. The payload carries
      // a `ReactFlowLayoutJson` whose nodes/edges each have a
      // `data.changeKind` tag. We:
      //   1. Cache the layout in the changeset-previews singleton so the
      //      <ChangesetPreviewCard> can pull it via `useStore`.
      //   2. Attach a `changeset-preview` envelope to the same assistant
      //      message that the kb-change-tree-shell envelope landed on.
      //
      // 77k1 Defect A defensive guard — same as `kb:changesetProposed`.
      // The preview store is workspace-global and is populated regardless,
      // so the Diff tab still has the layout; only the inline-card attach
      // is gated by the active session.
      const payload = asObject(event.data);
      const changesetId = stringField(payload, 'changesetId');
      const layoutRaw = payload?.layout;
      if (changesetId === null || layoutRaw === null || typeof layoutRaw !== 'object') {
        return false;
      }
      try {
        getChangesetPreviewsStore()
          .getState()
          .setLayout(changesetId, layoutRaw as ReactFlowLayoutJson);
      } catch {
        // Defensive — never let the store throw stop SSE dispatch.
      }
      if (!eventTargetsActiveSession(payload, opts)) return true;
      const targetMessageId = locateAttachTarget(state, opts);
      if (targetMessageId === null) return false;
      const envelope: InlineCardEnvelope = {
        kind: 'changeset-preview',
        props: { changesetId },
      };
      state.attachCard(targetMessageId, envelope);
      return true;
    }

    case 'validation:result': {
      // W3 fix-pass HIGH-1 — record the SSE-fanned validation result into
      // the default diagnostics store so the file-viewer's force-linting
      // subscription (FileEditor.tsx:217) re-fires CM6's linter source
      // function. The `path` field comes from the route's payload (the
      // route synthesises `<unsaved>.sysml` for text+uri requests; the
      // SPA's path-keyed lookup must therefore use the same key).
      const payload = asObject(event.data);
      const path = stringField(payload, 'path');
      if (path === null) return false;
      const diagsRaw = payload?.diagnostics;
      const diagnostics = Array.isArray(diagsRaw)
        ? (diagsRaw as Diagnostic[])
        : [];
      try {
        getDefaultDiagnosticsStore().getState().recordValidationResult({
          path,
          diagnostics,
        });
      } catch {
        // Defensive — store construction must never break SSE handling.
      }
      return true;
    }

    case 'files:changed': {
      // W3 fix-pass HIGH-4 — fan-out to:
      //  (a) save-gate per-mount subscribers (each `useSaveGate` invocation
      //      registers its `respondToFilesChanged` so the conflict-dialog
      //      can fire when the open file changes externally while dirty);
      //  (b) the default files store's `pulsePath` (1 s pulse-on-change
      //      UX for the file-tree per W3-S5 AC #6).
      const payload = asObject(event.data);
      const reasonRaw = stringField(payload, 'reason');
      const validReasons = ['external', 'applied', 'boot-scan'] as const;
      const reason = (validReasons as readonly string[]).includes(reasonRaw ?? '')
        ? (reasonRaw as 'external' | 'applied' | 'boot-scan')
        : null;
      const pathsRaw = payload?.paths;
      const paths =
        Array.isArray(pathsRaw) && pathsRaw.every((p) => typeof p === 'string')
          ? (pathsRaw as string[])
          : [];
      if (reason === null) return false;
      // (a) Notify every registered save-gate hook closure. Errors in one
      //     subscriber must not block the others.
      for (const subscriber of FILES_CHANGED_SUBSCRIBERS) {
        try {
          void subscriber({ reason, paths });
        } catch {
          // swallow; dispatcher continues.
        }
      }
      // (b) Fire the file-tree pulse UX for each affected path.
      try {
        const filesState = getFilesStore().getState();
        for (const p of paths) {
          filesState.pulsePath(p);
        }
      } catch {
        // Defensive — the singleton should always be available, but never
        // throw out of the dispatcher.
      }
      return true;
    }

    case 'kb:changesetApplied': {
      // W4 fix-pass HIGH-B / CSC-4 — fan the applier-confirmed outcomes
      // into the changeset slice (drives entity-status flips for the
      // <KbChangeTree> bubble + Diff tab) AND the kb slice (drives the
      // 1 s pulse animation in <KbEntityTree>). Both slices may be
      // unavailable in test stores that compose only chat/sse/inline-
      // cards; the partial-store guard above lets us no-op cleanly.
      const payload = asObject(event.data);
      const changesetId = stringField(payload, 'changesetId');
      if (changesetId === null) return false;
      const outcomesRaw = payload?.outcomes;
      if (!Array.isArray(outcomesRaw)) return false;
      const outcomes = outcomesRaw as KbApplyOutcome[];
      // KB-undo: forward `changesetComplete` so the slice can push the
      // changeset onto the available-undo ring buffer on the final emit
      // only. Defaults to `false` for backwards-compat with emitters
      // that omit the field (treated as in-progress).
      const changesetComplete = payload?.changesetComplete === true;

      const changesetApply = (state as Partial<ChangesetSlice>).applyChangesetApplied;
      if (typeof changesetApply === 'function') {
        try {
          changesetApply(changesetId, outcomes, { changesetComplete });
        } catch {
          // Defensive — never propagate slice errors out of the SSE loop.
        }
      }
      const kbApply = (state as Partial<KbSlice>).applyChangesetAppliedToKb;
      if (typeof kbApply === 'function') {
        try {
          kbApply(outcomes);
        } catch {
          // Defensive.
        }
      }
      return true;
    }

    case 'kb:changesetUndone': {
      // KB-undo SSE handler. The server has reverted the changeset and
      // restored / deleted the affected files. We:
      //  (1) tell the changeset slice to remove the entry from the
      //      available-undo stack (clearing any in-flight marker);
      //  (2) pulse every reverted/deleted path through the files store
      //      so the file-tree shows the change UX (same pattern as
      //      `files:changed`);
      //  (3) surface a `system:notice`-style notification so the user
      //      sees the round-trip confirmation.
      const payload = asObject(event.data);
      const changesetId = stringField(payload, 'changesetId');
      if (changesetId === null) return false;
      const revertedRaw = payload?.revertedFiles;
      const deletedRaw = payload?.deletedFiles;
      const reverted =
        Array.isArray(revertedRaw) && revertedRaw.every((p) => typeof p === 'string')
          ? (revertedRaw as string[])
          : [];
      const deleted =
        Array.isArray(deletedRaw) && deletedRaw.every((p) => typeof p === 'string')
          ? (deletedRaw as string[])
          : [];
      // (1) Slice fan-out — defensively guarded; partial test stores
      // omit this action.
      const applyUndone = (state as Partial<ChangesetSlice>).applyChangesetUndone;
      if (typeof applyUndone === 'function') {
        try {
          applyUndone(changesetId);
        } catch {
          // Defensive.
        }
      }
      // (2) File-tree pulse — same path as `files:changed`. Reverted +
      // deleted paths both pulse; the SPA's file-tree handles deletion
      // separately via the chokidar `files:changed` boot-scan.
      try {
        const filesState = getFilesStore().getState();
        for (const p of [...reverted, ...deleted]) {
          filesState.pulsePath(p);
        }
      } catch {
        // Defensive.
      }
      // (3) Brief toast. Use `appendSystemMessage` for parity with the
      // `system:notice` info path; the SPA renders these inline in chat.
      const totalFiles = reverted.length + deleted.length;
      const sid = opts.getActiveSessionId();
      if (sid !== null) {
        state.appendSystemMessage(
          sid,
          `Reverted ${totalFiles} file${totalFiles === 1 ? '' : 's'}.`,
        );
      }
      return true;
    }

    case 'session:created':
    case 'session:deleted':
    case 'session:updated': {
      // W2 fix-pass §5.1 — fan-out to registered subscribers (typically
      // the `<SessionList>` slice's `refreshSessions` after a debounce).
      // The chat slice itself doesn't track the sessions index (W2-S8
      // shipped sessions as a standalone vanilla store; the sessions
      // slice is not yet composed into the root store — see the W2
      // fix-pass report §1.2 for the deferral). Until the composition
      // lands, the broadcast informs any module-level listener that
      // calls `addSessionsEventSubscriber(...)` with a refresh handler.
      const payload = asObject(event.data);
      for (const subscriber of SESSIONS_EVENT_SUBSCRIBERS) {
        try {
          void subscriber({ kind: event.event, payload });
        } catch {
          // swallow; dispatcher continues.
        }
      }
      return true;
    }

    case ':keepalive':
    case 'keepalive':
      // NOOP — pure server heartbeat. Explicit case so the dispatcher's
      // intent is documented.
      return false;

    default:
      return false;
  }
}

// ─── files:changed subscriber registry (W3 fix-pass HIGH-4) ──────────────

/**
 * Hook closure registered by every `useSaveGate` mount so the SSE dispatcher
 * can deliver `files:changed` envelopes without a hard import on the hook
 * itself. The subscriber's `respondToFilesChanged` signature mirrors the
 * one returned by `useSaveGate(...)`.
 */
export type FilesChangedSubscriber = (input: {
  reason: 'external' | 'applied' | 'boot-scan';
  paths: string[];
}) => void | Promise<void>;

const FILES_CHANGED_SUBSCRIBERS = new Set<FilesChangedSubscriber>();

/**
 * Register a `files:changed` subscriber. Returns the unregister callback —
 * call it on `useEffect` cleanup. Multiple subscribers are supported (one per
 * mounted `<FileEditor>` if multi-pane lands later); each is invoked
 * independently so a single mis-behaving subscriber cannot starve others.
 */
export function addFilesChangedSubscriber(fn: FilesChangedSubscriber): () => void {
  FILES_CHANGED_SUBSCRIBERS.add(fn);
  return () => {
    FILES_CHANGED_SUBSCRIBERS.delete(fn);
  };
}

/** Test-only — wipe the subscriber set between cases. */
export function _resetFilesChangedSubscribersForTests(): void {
  FILES_CHANGED_SUBSCRIBERS.clear();
}

// ─── session:* subscriber registry (W2 fix-pass §5.1) ────────────────────

/** Discriminated event the registry forwards to subscribers. */
export type SessionsEventNotification =
  | { kind: 'session:created'; payload: Record<string, unknown> | null }
  | { kind: 'session:deleted'; payload: Record<string, unknown> | null }
  | { kind: 'session:updated'; payload: Record<string, unknown> | null };

/**
 * Hook closure for `<SessionList>` (or any other surface that needs to react
 * to sessions-index broadcasts) registered at mount time. The dispatcher
 * fans `session:created` / `session:deleted` / `session:updated` events
 * through this registry without a hard import on the sessions slice — same
 * pattern as `addFilesChangedSubscriber` for save-gate hooks.
 */
export type SessionsEventSubscriber = (
  notification: SessionsEventNotification,
) => void | Promise<void>;

const SESSIONS_EVENT_SUBSCRIBERS = new Set<SessionsEventSubscriber>();

/**
 * Register a `session:*` subscriber. Returns the unregister callback —
 * call it on `useEffect` cleanup. Multiple subscribers are supported.
 */
export function addSessionsEventSubscriber(fn: SessionsEventSubscriber): () => void {
  SESSIONS_EVENT_SUBSCRIBERS.add(fn);
  return () => {
    SESSIONS_EVENT_SUBSCRIBERS.delete(fn);
  };
}

/** Test-only — wipe the subscriber set between cases. */
export function _resetSessionsEventSubscribersForTests(): void {
  SESSIONS_EVENT_SUBSCRIBERS.clear();
}

// ─── Inline-card attachment helpers (W2-S6-integration) ──────────────────

/**
 * Resolve which assistant message to attach the next inline card to. Order:
 *   1. The currently-streaming message id for the active session.
 *   2. The pending assistant message id (set by `chat.sendMessage`).
 *   3. `null` — caller MUST drop the attachment (no live target).
 *
 * The slice's `attachCard` is idempotent on `(messageId, identityKey)` so
 * SSE replay across this resolver is safe: a retry that picks the same
 * message id will coalesce; a retry that picks a different id (e.g. after
 * the assistant message id changes mid-flight) will create a fresh card on
 * the new bubble — accepted as a known limitation since SSE replay should
 * not span message boundaries in normal operation.
 */
function locateAttachTarget(
  state: ChatSlice & SseSlice & InlineCardsSlice,
  opts: AttachOptions,
): string | null {
  const sid = opts.getActiveSessionId();
  if (sid === null) return null;
  const streamingId = state.streamingMessageIdBySession[sid] ?? null;
  if (streamingId !== null) return streamingId;
  const pendingId = state.pendingAssistantMessageIdBySession[sid] ?? null;
  return pendingId;
}

/**
 * IV3-S1 — resolve the message that PRODUCED an image turn, ONCE, from the
 * `turnCorrelationId`. This is the anti-wrong-bubble resolver: it binds the
 * skeleton card to the originating turn at `image:genStarted` time so a late
 * `image:genReady` (after `finishStream`, or after the user sent more
 * messages) never re-resolves to "the latest bubble".
 *
 * Resolution order:
 *   1. A message in `messagesById` whose `id` equals the `turnCorrelationId`
 *      (the durable / rehydrated path — the chat slice stamps a stable id
 *      keyed by the turn). This is robust to `finishStream` having cleared
 *      the streaming pointer and to intervening messages.
 *   2. Fall back to `locateAttachTarget` — the live streaming / pending
 *      bubble, which IS the producing turn at gen-START time (genStarted
 *      fires during the turn, before `finishStream`).
 *
 * Returns `null` only when neither resolves (no active session / no live
 * turn), in which case the caller drops the attachment.
 */
function locateTurnMessage(
  state: ChatSlice & SseSlice & InlineCardsSlice,
  turnCorrelationId: string,
  opts: AttachOptions,
): string | null {
  const sid = opts.getActiveSessionId();
  if (sid !== null) {
    const list = state.messagesById[sid] ?? [];
    const match = list.find((m) => m.id === turnCorrelationId);
    if (match !== undefined) return match.id;
  }
  // Fall back to the live streaming/pending bubble — correct during the
  // producing turn (genStarted fires before finishStream).
  return locateAttachTarget(state, opts);
}

/**
 * LV-raster fix — resolve the message that HOSTS a given view's card, matched
 * by the stable `viewId`. This is the attach resolver for the explicit
 * "Generate LLM visualization" button path (`POST /views/:viewId/image`), whose
 * `image:genStarted` fires long AFTER the producing turn finished with a fresh
 * server `randomUUID()` `turnCorrelationId` (matches no message) — so the
 * turn-correlation + streaming/pending fallbacks in `locateTurnMessage` all miss.
 *
 * The server sets `imageViewId === viewId`, and the message that produced the
 * view already carries a `response-view-proposal` card with `props.viewId`; an
 * earlier raster leaves an `illustrative-image` card with the same `viewId`. We
 * scan the ACTIVE session's messages (newest-first, so a re-generated view
 * resolves to its most recent card) for either, and return that message id.
 *
 * Active-session scoping is deliberate: a sessionId-less `image:genStarted` is
 * broadcast to every SPA client, but only the session that actually holds this
 * view's card mounts the skeleton — so a broadcast never contaminates a session
 * that never rendered the view. Returns `null` when no card for the view is
 * mounted (the caller then falls back to `locateTurnMessage`).
 */
function locateMessageHostingView(
  state: ChatSlice & SseSlice & InlineCardsSlice,
  imageViewId: string,
  opts: AttachOptions,
): string | null {
  const sid = opts.getActiveSessionId();
  if (sid === null) return null;
  const messages = state.messagesById[sid] ?? [];
  for (let i = messages.length - 1; i >= 0; i--) {
    const messageId = messages[i]?.id;
    if (messageId === undefined) continue;
    const cards = state.cardsByMessageId[messageId] ?? [];
    const hosts = cards.some(
      (c) =>
        (c.kind === 'response-view-proposal' || c.kind === 'illustrative-image') &&
        c.props.viewId === imageViewId,
    );
    if (hosts) return messageId;
  }
  return null;
}

/**
 * 77k1 Defect A guard — returns true when the SSE event's `sessionId`
 * matches the SPA's active session (or when the event omits sessionId,
 * which is the legacy / extension-emitter path that callers can't gate
 * on). Used by every inline-card-attaching kb:* handler so that
 * cross-session SSE replay landing during a session-switch race never
 * pollutes the newly-active session's bubble with the prior session's
 * card.
 *
 * Why permissive on missing `sessionId`: the in-process bus path
 * (extension host) does not stamp sessionId, and some workspace-global
 * events (e.g. `kb:changesetPreview` from a refresh) intentionally omit
 * it. Treating absent as "matches the active session" preserves the
 * legacy behaviour while still blocking the specific contamination
 * vector that motivated this guard (event tagged with sessionId X
 * arriving while the SPA has switched to session Y).
 */
function eventTargetsActiveSession(
  payload: Record<string, unknown> | null,
  opts: AttachOptions,
): boolean {
  const payloadSid = stringField(payload, 'sessionId');
  if (payloadSid === null) return true; // legacy / untagged emitter
  const activeSid = opts.getActiveSessionId();
  if (activeSid === null) return false; // no active session ⇒ drop
  return payloadSid === activeSid;
}

/**
 * LV3-S4 — parse an `engine-escalation` control notice message. The server emits
 * two related `system:notice` messages on the layout-escalation path:
 *
 *   • `engine-escalation:<reason>:<viewId>`
 *       — the view DID escalate; `<reason>` is `expressiveness-gap | frustration`
 *         and the SPA marks the originating card escalated with that reason.
 *   • `engine-escalation-suppressed:raster-off:<viewId>`
 *       — escalation was suppressed because the raster master switch is OFF; the
 *         deterministic sketch leads unchanged. We recognise it (so it is not
 *         appended as raw chat text) but DO NOT mark the card escalated
 *         (`reason: null`).
 *
 * `viewId` may itself contain `:` (rare, but ids are opaque); we therefore split
 * on the FIRST one/two delimiters and treat the remainder as the id. Returns
 * `null` when the message is not an engine-escalation control notice (the caller
 * then falls through to the normal `appendSystemMessage` path).
 */
export function parseEscalationNotice(
  message: string,
): { reason: 'expressiveness-gap' | 'frustration' | null; viewId: string } | null {
  // Suppressed form first (more specific prefix).
  const SUPPRESSED = 'engine-escalation-suppressed:raster-off:';
  if (message.startsWith(SUPPRESSED)) {
    const viewId = message.slice(SUPPRESSED.length);
    if (viewId.length === 0) return null;
    return { reason: null, viewId };
  }
  const PREFIX = 'engine-escalation:';
  if (!message.startsWith(PREFIX)) return null;
  const rest = message.slice(PREFIX.length);
  const sep = rest.indexOf(':');
  if (sep <= 0) return null;
  const reasonRaw = rest.slice(0, sep);
  const viewId = rest.slice(sep + 1);
  if (viewId.length === 0) return null;
  if (reasonRaw !== 'expressiveness-gap' && reasonRaw !== 'frustration') {
    return null;
  }
  return { reason: reasonRaw, viewId };
}

/**
 * LV3-S4 — find the response-view card for `viewId` across ALL message bubbles
 * and patch its envelope with `escalated=<reason>` + a bumped `layoutRevision`.
 *
 * The escalation `system:notice` is broadcast (no reliable message-bubble
 * binding), and the escalated view is the CURRENT view of the session — already
 * mounted on a prior turn's bubble — so we scan every bubble's card list rather
 * than only the live attach target. The bumped `layoutRevision` re-keys the
 * card's one-shot fetch guard (the same mechanism the relayout re-emit uses), so
 * the escalation reason is part of what re-reaches the frozen card. Idempotent:
 * patching an already-escalated card with the same reason still re-renders but
 * carries no new state beyond the revision bump (harmless). No-op when no
 * matching card exists (the notice may precede the card on a cold replay).
 */
function markCardEscalated(
  state: InlineCardsSlice,
  viewId: string,
  reason: 'expressiveness-gap' | 'frustration',
): void {
  for (const [messageId, cards] of Object.entries(state.cardsByMessageId)) {
    const index = cards.findIndex(
      (e) => e.kind === 'response-view-proposal' && e.props.viewId === viewId,
    );
    if (index < 0) continue;
    const existing = cards[index];
    if (existing === undefined || existing.kind !== 'response-view-proposal') {
      continue;
    }
    const priorRevision =
      typeof existing.props.layoutRevision === 'number'
        ? existing.props.layoutRevision
        : 0;
    state.setCardProps(messageId, index, {
      escalated: reason,
      layoutRevision: priorRevision + 1,
    });
    // A given viewId is mounted on exactly one bubble; stop after the match.
    return;
  }
}

/**
 * Try to extract a `clarification` envelope from a `chat:toolResult` output.
 * The orchestrator's `apply_model_change` tool returns a SUCCESS result whose
 * `value` is a JSON string `{stage: 'clarification', clarificationNeeded:
 * true, options: string[]}`. Some transports unwrap the JSON before the
 * SSE hop, so we accept both an object and a stringified-JSON form.
 *
 * Returns `null` when the output is not a clarification payload.
 */
function extractClarificationEnvelope(
  output: unknown,
): InlineCardEnvelope | null {
  let parsed: Record<string, unknown> | null = null;
  if (typeof output === 'string') {
    try {
      const raw = JSON.parse(output) as unknown;
      if (raw !== null && typeof raw === 'object' && !Array.isArray(raw)) {
        parsed = raw as Record<string, unknown>;
      }
    } catch {
      return null;
    }
  } else if (
    output !== null &&
    typeof output === 'object' &&
    !Array.isArray(output)
  ) {
    parsed = output as Record<string, unknown>;
  }
  if (parsed === null) return null;
  if (parsed.clarificationNeeded !== true) return null;
  // `options` is the spec'd field (apply_model_change-tool.ts:184). Treat
  // the first option as the canonical question text per the Lit precedent
  // (`webview-message.ts:265 → clarification: { options }`); remaining
  // options become the chip choices.
  const optsRaw = parsed.options;
  const options =
    Array.isArray(optsRaw) && optsRaw.every((s) => typeof s === 'string')
      ? (optsRaw as string[])
      : [];
  if (options.length === 0) return null;
  const [question, ...rest] = options;
  if (typeof question !== 'string' || question.length === 0) return null;
  return {
    kind: 'clarification',
    props: rest.length > 0 ? { question, options: rest } : { question },
  };
}

/** Subscribe to a stream of `SseEvent`s and forward to the chat slice. */
export function attachSseToChatSlice(
  store: ChatLikeStore,
  emitter: { onEvent: (handler: (e: SseEvent) => void) => () => void },
  opts: AttachOptions,
): () => void {
  return emitter.onEvent((e) => {
    dispatchSseEvent(store, e, opts);
  });
}

// ─── EventLog fan-out helper (migration final fix-pass — HIGH-3) ─────────

/**
 * Map an SSE event name onto the {@link EventLogSource} classification used
 * by the Activity tab's badge + the Debug tab's per-source counters. The
 * mapping is intentionally conservative: keep-alive frames are NOT recorded
 * (pure server heartbeats add noise to the user-facing log).
 */
export function classifyEventSource(eventName: string): EventLogSource | null {
  if (eventName.startsWith('chat:')) return 'chat';
  if (eventName.startsWith('kb:viewRendered')) return 'view';
  // IV3-S1 — image gen events are visualization-view events.
  if (eventName.startsWith('image:')) return 'view';
  if (eventName.startsWith('kb:')) return 'kb';
  if (eventName.startsWith('validation:')) return 'validation';
  if (eventName.startsWith('files:')) return 'files';
  if (eventName.startsWith('session:')) return 'session';
  if (eventName === 'system:notice' || eventName === 'error') return 'system';
  return null;
}

/**
 * Append an SSE event into the eventLog slice when the slice is composed
 * into the host store. Defensive — partial test stores that omit the slice
 * silently no-op; failures inside the slice never propagate out of the SSE
 * dispatcher.
 */
function appendToEventLogIfAvailable(
  state: { appendEventLog?: EventLogSlice['appendEventLog'] },
  event: SseEvent,
): void {
  // Skip pure heartbeats — their cadence would crowd the log.
  if (event.event === ':keepalive' || event.event === 'keepalive') return;
  const source = classifyEventSource(event.event);
  if (source === null) return;
  const append = state.appendEventLog;
  if (typeof append !== 'function') return;
  try {
    append({ type: event.event, source, payload: event.data });
  } catch {
    // Defensive — never break the SSE dispatcher because of a logging
    // slice failure.
  }
}

// ─── Settings reader (LV3-S1) ────────────────────────────────────────────

/**
 * Read the effective `views.llmImage.enabled` master toggle from the shared
 * merged-settings store (the SAME store `useContextWindowForModel` reads, kept
 * fresh by `App.tsx`'s `loadMergedSettings()`). Returns the resolved value when
 * present, else the schema default (`false`). Defaults to `false` on any
 * absence / malformed entry so the "Generate LLM visualization" button is never
 * offered when the setting can't be confirmed ON. Pure + defensive — never
 * throws out of the SSE dispatch loop.
 */
function readLlmImageEnabled(): boolean {
  try {
    const entry = selectMergedByKey(
      getDefaultSettingsStore().getState(),
      qualifiedKey('views.llmImage.enabled'),
    );
    if (entry === undefined) return false;
    const raw = entry.value !== undefined ? entry.value : entry.defaultValue;
    return raw === true;
  } catch {
    return false;
  }
}

// ─── Type helpers ───────────────────────────────────────────────────────

function asObject(value: unknown): Record<string, unknown> | null {
  if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return null;
}

function stringField(obj: Record<string, unknown> | null, key: string): string | null {
  if (obj === null) return null;
  const v = obj[key];
  return typeof v === 'string' ? v : null;
}
